import {
  AiChat02Icon,
  AlertCircleIcon,
  ArrowRight01Icon,
  ChartBarLineIcon,
  CheckmarkCircle02Icon,
  Delete02Icon,
  Download01Icon,
  Flag02Icon,
  Globe02Icon,
  Logout01Icon,
  MoreHorizontalIcon,
  RankingIcon,
  Search01Icon,
  SentIcon,
  Settings02Icon,
  ShieldUserIcon,
  UserBlock02Icon,
  UserGroupIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useEffect, useState } from 'react';
import type { SubjectPlanSummary } from '../domain/platform';
import { AccentPicker } from './AccentPicker';
import {
  deleteAccount,
  exportAccountData,
  loadAdminAudit,
  loadAdminOverview,
  loadAdminRoadmaps,
  loadAdminReports,
  loadAdminUser,
  loadAdminUsers,
  loadAiHistory,
  loadLeaderboard,
  loadSettings,
  reportLearner,
  saveSettings,
  sendAiMessage,
  updateAdminReport,
  updateAdminUser,
  type AdminAuditAction,
  type AdminOverview,
  type AdminReport,
  type AdminRoadmapEntry,
  type AdminRoadmapFeedback,
  type AdminUserSummary,
  type AiAllowance,
  type AiMessage,
  type LeaderboardEntry,
  type ProfileSettings,
  type SessionUser,
} from './api';
import { applyAccent } from './theme';

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function PanelHeader({
  eyebrow,
  title,
  detail,
}: {
  eyebrow: string;
  title: string;
  detail?: string;
}) {
  return (
    <header className="view-header product-panel-header">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h2>{title}</h2>
        {detail && <p>{detail}</p>}
      </div>
    </header>
  );
}

export function AssistantView({ planId, subject }: { planId: string; subject: string }) {
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AiMessage[]>([]);
  const [allowance, setAllowance] = useState<AiAllowance | null>(null);
  const [message, setMessage] = useState('');
  const [useWeb, setUseWeb] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    void loadAiHistory(planId)
      .then((history) => {
        if (!live) return;
        setThreadId(history.threadId);
        setMessages(history.messages);
        setAllowance(history.allowance);
      })
      .catch((reason) => live && setError(errorMessage(reason, 'Could not load the assistant.')))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [planId]);

  async function submit(nextMessage = message) {
    const content = nextMessage.trim();
    if (!content || sending || !allowance?.remaining) return;
    setSending(true);
    setError(null);
    setMessage('');
    const optimistic: AiMessage = { role: 'user', content };
    setMessages((current) => [...current, optimistic].slice(-10));
    try {
      const result = await sendAiMessage({
        ...(threadId ? { threadId } : {}),
        planId,
        message: content,
        useWeb,
      });
      setThreadId(result.threadId);
      setMessages((current) => [...current, result.message].slice(-10));
      setAllowance(result.allowance);
    } catch (reason) {
      setMessages((current) => current.filter((item) => item !== optimistic));
      setMessage(content);
      setError(errorMessage(reason, 'The assistant could not answer.'));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="product-panel assistant-panel">
      <PanelHeader
        eyebrow="Study assistant"
        title={subject}
        detail="Plan, clarify, and revise with your current calendar in context."
      />
      <section className="assistant-shell">
        <div className="assistant-meta">
          <span>
            <HugeiconsIcon icon={AiChat02Icon} size={15} /> Five-message memory
          </span>
          <span className={allowance?.remaining === 0 ? 'quota-empty' : ''}>
            {allowance ? `${allowance.remaining} of ${allowance.limit} left` : 'Loading allowance'}
          </span>
        </div>

        <div className="assistant-messages" aria-live="polite" aria-busy={loading || sending}>
          {loading ? (
            <PanelLoading label="Opening your thread" />
          ) : messages.length ? (
            messages.map((item, index) => (
              <article className={`assistant-message ${item.role}`} key={`${item.role}-${index}`}>
                <span>{item.role === 'assistant' ? 'Tarpy' : 'You'}</span>
                <p>{item.content}</p>
              </article>
            ))
          ) : (
            <div className="panel-empty assistant-empty">
              <HugeiconsIcon icon={AiChat02Icon} size={23} />
              <h3>Ask from the plan you are viewing.</h3>
              <p>No generic chat history. This thread stays with {subject}.</p>
              <div className="prompt-chips">
                {[
                  'What should I revise next?',
                  'Make today realistic',
                  'Quiz my weakest topic',
                ].map((prompt) => (
                  <button type="button" key={prompt} onClick={() => void submit(prompt)}>
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}
          {sending && (
            <article className="assistant-message assistant pending-message">
              <span>Tarpy</span>
              <i /> <i /> <i />
            </article>
          )}
        </div>

        {error && (
          <div className="inline-error" role="alert">
            <HugeiconsIcon icon={AlertCircleIcon} size={16} /> {error}
          </div>
        )}

        <form
          className="assistant-composer"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={`Ask about ${subject}…`}
            aria-label="Message Tarpy"
            rows={2}
            maxLength={4000}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void submit();
              }
            }}
          />
          <div className="composer-actions">
            <label className="research-toggle">
              <input
                type="checkbox"
                checked={useWeb}
                onChange={(event) => setUseWeb(event.target.checked)}
              />
              <HugeiconsIcon icon={Globe02Icon} size={15} /> Web research
            </label>
            <button
              className="primary-button compact-button"
              type="submit"
              disabled={sending || !message.trim() || allowance?.remaining === 0}
            >
              <HugeiconsIcon icon={SentIcon} size={15} /> Send
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export function LeaderboardView({
  currentUserId,
  canReport = true,
  publicPage = false,
}: {
  currentUserId?: string;
  canReport?: boolean;
  publicPage?: boolean;
}) {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reporting, setReporting] = useState<LeaderboardEntry | null>(null);
  const [reason, setReason] = useState<'cheating' | 'harassment' | 'impersonation' | 'other'>(
    'cheating',
  );
  const [details, setDetails] = useState('');
  const [reportStatus, setReportStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

  useEffect(() => {
    void loadLeaderboard()
      .then(setEntries)
      .catch((reason) => setError(errorMessage(reason, 'Could not load the leaderboard.')))
      .finally(() => setLoading(false));
  }, []);

  async function submitReport() {
    if (!reporting || details.trim().length < 5) return;
    setReportStatus('sending');
    setError(null);
    try {
      await reportLearner({ targetUserId: reporting.userId, reason, details: details.trim() });
      setReportStatus('sent');
    } catch (reportError) {
      setReportStatus('idle');
      setError(errorMessage(reportError, 'Could not send the report.'));
    }
  }

  const content = (
    <>
      {!publicPage && (
        <PanelHeader
          eyebrow="Community"
          title="Leaderboard"
          detail="Consistency and accuracy—not raw check-ins."
        />
      )}
      {error && (
        <div className="inline-error" role="alert">
          <HugeiconsIcon icon={AlertCircleIcon} size={16} /> {error}
        </div>
      )}
      {loading ? (
        <PanelLoading label="Calculating standings" />
      ) : entries.length === 0 ? (
        <div className="panel-empty leaderboard-empty">
          <HugeiconsIcon icon={RankingIcon} size={24} />
          <h3>No public scores yet.</h3>
          <p>The board begins when learners opt in and complete real work.</p>
        </div>
      ) : (
        <div className="leaderboard-table" role="table" aria-label="Public leaderboard">
          <div className="leaderboard-head" role="row">
            <span>Rank</span>
            <span>Learner</span>
            <span>Streak</span>
            <span>Accuracy</span>
            <span>Points</span>
            {canReport && <span />}
          </div>
          {entries.map((entry) => {
            const own = entry.userId === currentUserId;
            return (
              <div
                className={`leaderboard-row ${own ? 'own-row' : ''}`}
                role="row"
                key={entry.userId}
              >
                <strong className="rank-cell">#{entry.rank}</strong>
                <span className="learner-cell">
                  <b>{entry.username}</b>
                  <small>{entry.grade ? `Class ${entry.grade}` : 'Learner'}</small>
                </span>
                <span>{entry.streakDays}d</span>
                <span>{entry.averageAccuracy === null ? '—' : `${entry.averageAccuracy}%`}</span>
                <strong>{entry.points.toLocaleString()}</strong>
                {canReport && (
                  <span>
                    {own ? (
                      <small>You</small>
                    ) : (
                      <button
                        className="table-action"
                        type="button"
                        onClick={() => {
                          setReporting(entry);
                          setReportStatus('idle');
                          setDetails('');
                        }}
                        aria-label={`Report ${entry.username}`}
                      >
                        <HugeiconsIcon icon={Flag02Icon} size={14} /> Report
                      </button>
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {reporting && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setReporting(null)}>
          <section
            className="modal-card report-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            {reportStatus === 'sent' ? (
              <div className="report-success">
                <HugeiconsIcon icon={CheckmarkCircle02Icon} size={27} />
                <h3 id="report-title">Report received.</h3>
                <p>An administrator can review the evidence and action history.</p>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setReporting(null)}
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <span className="eyebrow">Fair play</span>
                <h3 id="report-title">Report {reporting.username}</h3>
                <label>
                  Reason
                  <select
                    value={reason}
                    onChange={(event) => setReason(event.target.value as typeof reason)}
                  >
                    <option value="cheating">Suspicious progress</option>
                    <option value="impersonation">Impersonation</option>
                    <option value="harassment">Harassment</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label>
                  What did you notice?
                  <textarea
                    rows={4}
                    value={details}
                    maxLength={1000}
                    onChange={(event) => setDetails(event.target.value)}
                    placeholder="Give specific, factual details."
                  />
                </label>
                <div className="dialog-actions">
                  <button className="text-button" type="button" onClick={() => setReporting(null)}>
                    Cancel
                  </button>
                  <button
                    className="primary-button"
                    type="button"
                    disabled={details.trim().length < 5 || reportStatus === 'sending'}
                    onClick={() => void submitReport()}
                  >
                    Send report
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </>
  );

  return publicPage ? content : <div className="product-panel leaderboard-panel">{content}</div>;
}

export function SettingsView({
  user,
  plans,
  onSaved,
  onAutoRebalanceChange,
  onSignOut,
  signingOut,
}: {
  user: SessionUser;
  plans: SubjectPlanSummary[];
  onSaved: (profile: ProfileSettings) => void;
  onAutoRebalanceChange: (planId: string, enabled: boolean) => Promise<void>;
  onSignOut: () => Promise<void>;
  signingOut: boolean;
}) {
  const [profile, setProfile] = useState<ProfileSettings | null>(null);
  const [aiUsage, setAiUsage] = useState<{ used: number; limit: number; period: string } | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    void loadSettings()
      .then((settings) => {
        setProfile(settings.profile);
        setAiUsage(settings.ai);
      })
      .catch((reason) => setError(errorMessage(reason, 'Could not load settings.')));
  }, []);

  async function submit() {
    if (!profile) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await saveSettings(profile);
      onSaved(profile);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2400);
    } catch (reason) {
      setError(errorMessage(reason, 'Could not save settings.'));
    } finally {
      setSaving(false);
    }
  }

  async function downloadData() {
    setExporting(true);
    setError(null);
    try {
      const blob = await exportAccountData();
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `tarpy-data-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (reason) {
      setError(errorMessage(reason, 'Could not export account data.'));
    } finally {
      setExporting(false);
    }
  }

  async function removeAccount() {
    if (deleteConfirmation !== 'DELETE') return;
    setDeleting(true);
    setError(null);
    try {
      await deleteAccount();
      window.location.assign('/');
    } catch (reason) {
      setError(errorMessage(reason, 'Could not delete the account.'));
      setDeleting(false);
    }
  }

  return (
    <div className="product-panel account-settings-panel">
      <PanelHeader
        eyebrow="Account"
        title="Settings"
        detail="Profile, planning defaults, and privacy."
      />
      {!profile ? (
        <PanelLoading label="Loading settings" />
      ) : (
        <div className="settings-grid">
          <section className="settings-card">
            <div className="settings-card-title">
              <HugeiconsIcon icon={Settings02Icon} size={17} />
              <div>
                <h3>Study profile</h3>
                <p>Used when calendars and assistant answers are generated.</p>
              </div>
            </div>
            <div className="form-grid two-column">
              <label>
                Username
                <input
                  value={profile.username}
                  minLength={2}
                  maxLength={40}
                  onChange={(event) => setProfile({ ...profile, username: event.target.value })}
                />
              </label>
              <label>
                Class
                <select
                  value={profile.grade}
                  onChange={(event) =>
                    setProfile({
                      ...profile,
                      grade: Number(event.target.value) as ProfileSettings['grade'],
                    })
                  }
                >
                  {[9, 10, 11, 12].map((grade) => (
                    <option key={grade} value={grade}>
                      Class {grade}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Board
                <select
                  value={profile.curriculum}
                  onChange={(event) =>
                    setProfile({
                      ...profile,
                      curriculum: event.target.value as ProfileSettings['curriculum'],
                    })
                  }
                >
                  {['CBSE', 'ICSE', 'ISC', 'STATE', 'IB', 'OTHER'].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                Goal
                <select
                  value={profile.examTrack}
                  onChange={(event) =>
                    setProfile({
                      ...profile,
                      examTrack: event.target.value as ProfileSettings['examTrack'],
                    })
                  }
                >
                  {['SCHOOL', 'BOARDS', 'JEE', 'NEET', 'CUET', 'OLYMPIAD', 'OTHER'].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label className="wide-field">
                Timezone
                <input
                  value={profile.timezone}
                  maxLength={80}
                  onChange={(event) => setProfile({ ...profile, timezone: event.target.value })}
                />
              </label>
            </div>
            <AccentPicker
              value={profile.accentColor}
              onChange={(accentColor) => {
                applyAccent(accentColor);
                setProfile({ ...profile, accentColor });
              }}
            />
          </section>

          <section className="settings-card">
            <div className="settings-card-title">
              <HugeiconsIcon icon={ChartBarLineIcon} size={17} />
              <div>
                <h3>Calendar behavior</h3>
                <p>Automation suggests changes. You can still override every session.</p>
              </div>
            </div>
            <div className="plan-setting-list">
              {plans.map((plan) => (
                <label className="setting-row" key={plan.id}>
                  <span>
                    <b>{plan.title}</b>
                    <small>Redistribute missed work through the remaining month</small>
                  </span>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={plan.autoRebalance}
                    onChange={(event) => void onAutoRebalanceChange(plan.id, event.target.checked)}
                    aria-label={`Auto-rebalance ${plan.title}`}
                  />
                </label>
              ))}
            </div>
          </section>

          <section className="settings-card compact-settings-card">
            <div className="settings-card-title">
              <HugeiconsIcon icon={RankingIcon} size={17} />
              <div>
                <h3>Public profile</h3>
                <p>Email and Google profile details are never shown.</p>
              </div>
            </div>
            <label className="setting-row">
              <span>
                <b>Show me on the leaderboard</b>
                <small>Only username, class, streak, accuracy, and points</small>
              </span>
              <input
                type="checkbox"
                role="switch"
                checked={profile.leaderboardVisible}
                onChange={(event) =>
                  setProfile({ ...profile, leaderboardVisible: event.target.checked })
                }
              />
            </label>
          </section>

          <section className="settings-card compact-settings-card usage-card">
            <div className="settings-card-title">
              <HugeiconsIcon icon={AiChat02Icon} size={17} />
              <div>
                <h3>Assistant allowance</h3>
                <p>{aiUsage ? `${aiUsage.used} used in ${aiUsage.period}` : 'Loading usage'}</p>
              </div>
            </div>
            <strong>
              {aiUsage ? `${Math.max(0, aiUsage.limit - aiUsage.used)} / ${aiUsage.limit}` : '—'}
            </strong>
          </section>

          <section className="settings-card account-data-card">
            <div className="settings-card-title">
              <HugeiconsIcon icon={Download01Icon} size={17} />
              <div>
                <h3>Data & account</h3>
                <p>Download your complete record or permanently remove your account.</p>
              </div>
            </div>
            <div className="account-data-actions">
              <button
                className="secondary-button"
                type="button"
                disabled={signingOut}
                onClick={() => void onSignOut()}
              >
                <HugeiconsIcon icon={Logout01Icon} size={15} />
                {signingOut ? 'Logging out…' : 'Log out'}
              </button>
              <button
                className="secondary-button"
                type="button"
                disabled={exporting}
                onClick={() => void downloadData()}
              >
                <HugeiconsIcon icon={Download01Icon} size={15} />
                {exporting ? 'Preparing…' : 'Download my data'}
              </button>
              {!user.isAdmin && !deleteOpen && (
                <button className="danger-button" type="button" onClick={() => setDeleteOpen(true)}>
                  <HugeiconsIcon icon={Delete02Icon} size={15} /> Delete account
                </button>
              )}
            </div>
            {user.isAdmin && (
              <p className="protected-account-copy">
                Administrator deletion requires an ownership transfer.
              </p>
            )}
            {deleteOpen && (
              <div className="delete-account-confirmation">
                <p>
                  This permanently deletes your plans, activity, assistant history, and reports.
                </p>
                <label>
                  Type DELETE to confirm
                  <input
                    value={deleteConfirmation}
                    autoComplete="off"
                    onChange={(event) => setDeleteConfirmation(event.target.value)}
                  />
                </label>
                <div>
                  <button
                    className="text-button"
                    type="button"
                    onClick={() => {
                      setDeleteOpen(false);
                      setDeleteConfirmation('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="danger-button"
                    type="button"
                    disabled={deleteConfirmation !== 'DELETE' || deleting}
                    onClick={() => void removeAccount()}
                  >
                    {deleting ? 'Deleting…' : 'Delete permanently'}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
      {error && (
        <div className="inline-error" role="alert">
          <HugeiconsIcon icon={AlertCircleIcon} size={16} /> {error}
        </div>
      )}
      {profile && (
        <div className="sticky-save-bar">
          <span>{saved ? 'Changes saved' : `${user.email} · private`}</span>
          <button
            className="primary-button compact-button"
            type="button"
            disabled={saving}
            onClick={() => void submit()}
          >
            {saved && <HugeiconsIcon icon={CheckmarkCircle02Icon} size={15} />}
            {saving ? 'Saving…' : saved ? 'Saved' : 'Save settings'}
          </button>
        </div>
      )}
    </div>
  );
}

type AdminSection = 'overview' | 'users' | 'reports' | 'roadmaps' | 'audit';

export function AdminView() {
  const [section, setSection] = useState<AdminSection>('overview');
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [reports, setReports] = useState<AdminReport[]>([]);
  const [audit, setAudit] = useState<AdminAuditAction[]>([]);
  const [roadmapFeedback, setRoadmapFeedback] = useState<AdminRoadmapFeedback[]>([]);
  const [roadmapEntries, setRoadmapEntries] = useState<AdminRoadmapEntry[]>([]);
  const [selected, setSelected] = useState<AdminUserSummary | null>(null);
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof loadAdminUser>> | null>(null);
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(35);
  const [banReason, setBanReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [nextOverview, nextUsers, nextReports, nextAudit, nextRoadmaps] = await Promise.all([
        loadAdminOverview(),
        loadAdminUsers(query),
        loadAdminReports(),
        loadAdminAudit(),
        loadAdminRoadmaps(),
      ]);
      setOverview(nextOverview);
      setUsers(nextUsers);
      setReports(nextReports);
      setAudit(nextAudit);
      setRoadmapFeedback(nextRoadmaps.feedback);
      setRoadmapEntries(nextRoadmaps.entries);
    } catch (reason) {
      setError(errorMessage(reason, 'Could not load the admin workspace.'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // The search form refreshes explicitly so typing never floods the API.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openUser(user: AdminUserSummary) {
    setSelected(user);
    setLimit(user.ai_monthly_limit);
    setBanReason(user.banned_reason ?? '');
    setDetail(null);
    try {
      setDetail(await loadAdminUser(user.id));
    } catch (reason) {
      setError(errorMessage(reason, 'Could not inspect this user.'));
    }
  }

  async function actOnUser(input: {
    banned?: boolean;
    banReason?: string;
    aiMonthlyLimit?: number;
  }) {
    if (!selected) return;
    setError(null);
    try {
      await updateAdminUser(selected.id, input);
      await refresh();
      const updated = (await loadAdminUsers(query)).find((user) => user.id === selected.id);
      if (updated) await openUser(updated);
    } catch (reason) {
      setError(errorMessage(reason, 'The admin action failed.'));
    }
  }

  async function actOnReport(report: AdminReport, status: 'reviewed' | 'actioned' | 'dismissed') {
    try {
      await updateAdminReport(report.id, { status, note: '' });
      await refresh();
    } catch (reason) {
      setError(errorMessage(reason, 'The report action failed.'));
    }
  }

  const metrics = overview
    ? [
        ['Users', overview.users],
        ['Onboarded', overview.onboardedUsers],
        ['Active accounts', overview.users - overview.bannedUsers],
        ['Active 7d', overview.activeUsers7d],
        ['Public profiles', overview.publicProfiles],
        ['Subject plans', overview.subjectPlans],
        ['AI this month', overview.aiRequestsThisMonth],
        ['AI failures', overview.failedAiThisMonth],
        ['Open reports', overview.openReports],
        ['Roadmap entries', overview.roadmapEntries],
        ['Roadmap corrections', overview.roadmapCorrections],
        ['Completed sessions', overview.completedSessions],
        ['Total points', overview.totalPoints],
      ]
    : [];

  return (
    <div className="product-panel admin-panel">
      <PanelHeader
        eyebrow="Restricted"
        title="Admin"
        detail="Users, usage, reports, and accountable actions."
      />
      <div className="admin-tabs" role="tablist">
        {(['overview', 'users', 'reports', 'roadmaps', 'audit'] as AdminSection[]).map((item) => (
          <button
            type="button"
            role="tab"
            aria-selected={section === item}
            className={section === item ? 'active' : ''}
            key={item}
            onClick={() => setSection(item)}
          >
            {item}
            {item === 'reports' && overview?.openReports ? (
              <span>{overview.openReports}</span>
            ) : null}
          </button>
        ))}
      </div>
      {error && (
        <div className="inline-error" role="alert">
          <HugeiconsIcon icon={AlertCircleIcon} size={16} /> {error}
        </div>
      )}
      {loading && !overview ? (
        <PanelLoading label="Loading admin data" />
      ) : section === 'overview' ? (
        <>
          <div className="admin-metrics">
            {metrics.map(([label, value]) => (
              <article key={label}>
                <span>{label}</span>
                <strong>{Number(value).toLocaleString()}</strong>
              </article>
            ))}
          </div>
          <section className="admin-summary-card">
            <HugeiconsIcon icon={ShieldUserIcon} size={22} />
            <div>
              <h3>Every mutation is audited.</h3>
              <p>Bans, allowance changes, and report decisions record the acting administrator.</p>
            </div>
          </section>
        </>
      ) : section === 'users' ? (
        <div className="admin-users-layout">
          <section className="admin-list-card">
            <form
              className="admin-search"
              onSubmit={(event) => {
                event.preventDefault();
                void refresh();
              }}
            >
              <HugeiconsIcon icon={Search01Icon} size={16} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Email, username, or name"
                aria-label="Search users"
              />
              <button type="submit">Search</button>
            </form>
            <div className="admin-user-list">
              {users.map((adminUser) => (
                <button
                  type="button"
                  className={selected?.id === adminUser.id ? 'active' : ''}
                  key={adminUser.id}
                  onClick={() => void openUser(adminUser)}
                >
                  <span>
                    <b>{adminUser.username || adminUser.display_name}</b>
                    <small>{adminUser.email}</small>
                  </span>
                  <span>
                    <b>{adminUser.points} pts</b>
                    <small>{adminUser.plan_count} plans</small>
                  </span>
                  <HugeiconsIcon icon={ArrowRight01Icon} size={14} />
                </button>
              ))}
              {!users.length && (
                <div className="panel-empty compact-empty">
                  <HugeiconsIcon icon={UserGroupIcon} size={22} />
                  <p>No users match.</p>
                </div>
              )}
            </div>
          </section>
          <section className="admin-detail-card">
            {!selected ? (
              <div className="panel-empty compact-empty">
                <HugeiconsIcon icon={UserGroupIcon} size={22} />
                <p>Select a user to inspect their complete account record.</p>
              </div>
            ) : (
              <>
                <div className="admin-detail-heading">
                  <span>
                    <h3>{selected.username || selected.display_name}</h3>
                    <p>{selected.email}</p>
                  </span>
                  <span className={`status-pill ${selected.banned_at ? 'danger' : 'success'}`}>
                    {selected.banned_at ? 'Suspended' : 'Active'}
                  </span>
                </div>
                <dl className="admin-facts">
                  <div>
                    <dt>Class</dt>
                    <dd>{selected.grade_level ?? '—'}</dd>
                  </div>
                  <div>
                    <dt>Board</dt>
                    <dd>{selected.curriculum ?? '—'}</dd>
                  </div>
                  <div>
                    <dt>Goal</dt>
                    <dd>{selected.exam_track ?? '—'}</dd>
                  </div>
                  <div>
                    <dt>Joined</dt>
                    <dd>{new Date(selected.created_at).toLocaleDateString()}</dd>
                  </div>
                </dl>
                <label className="admin-field">
                  Monthly AI limit
                  <input
                    type="number"
                    min="0"
                    max="10000"
                    value={limit}
                    onChange={(event) => setLimit(Number(event.target.value))}
                  />
                </label>
                <button
                  className="secondary-button full-button"
                  type="button"
                  onClick={() => void actOnUser({ aiMonthlyLimit: limit })}
                >
                  Update allowance
                </button>
                <label className="admin-field">
                  Moderation note
                  <textarea
                    rows={3}
                    value={banReason}
                    onChange={(event) => setBanReason(event.target.value)}
                    placeholder="Reason visible in the audit record"
                  />
                </label>
                {selected.banned_at ? (
                  <button
                    className="secondary-button full-button"
                    type="button"
                    onClick={() => void actOnUser({ banned: false })}
                  >
                    Restore account
                  </button>
                ) : (
                  <button
                    className="danger-button full-button"
                    type="button"
                    disabled={!banReason.trim()}
                    onClick={() => void actOnUser({ banned: true, banReason: banReason.trim() })}
                  >
                    <HugeiconsIcon icon={UserBlock02Icon} size={15} /> Suspend account
                  </button>
                )}
                {detail ? (
                  <>
                    <div className="admin-record-summary">
                      <span>
                        {detail.plans.length}
                        <small>plans</small>
                      </span>
                      <span>
                        {detail.usage.length}
                        <small>AI events</small>
                      </span>
                      <span>
                        {detail.reports.length}
                        <small>reports</small>
                      </span>
                      <span>
                        {detail.actions.length}
                        <small>admin actions</small>
                      </span>
                    </div>
                    {[
                      ['Subject plan records', detail.plans],
                      ['AI usage records', detail.usage],
                      ['Report records', detail.reports],
                      ['Admin action records', detail.actions],
                    ].map(([label, records]) => (
                      <details className="admin-data-disclosure" key={label as string}>
                        <summary>
                          {label as string} <span>{(records as Array<unknown>).length}</span>
                        </summary>
                        <pre>{JSON.stringify(records, null, 2)}</pre>
                      </details>
                    ))}
                  </>
                ) : (
                  <PanelLoading label="Loading record" />
                )}
              </>
            )}
          </section>
        </div>
      ) : section === 'reports' ? (
        <div className="report-queue">
          {reports.map((report) => (
            <article key={report.id}>
              <header>
                <span className={`status-pill ${report.status}`}>{report.status}</span>
                <time>{new Date(report.created_at).toLocaleString()}</time>
              </header>
              <h3>{report.reason.replace('_', ' ')}</h3>
              <p>{report.details}</p>
              <small>
                {report.reporter_username || 'Learner'} reported{' '}
                {report.target_username || report.target_email}
              </small>
              <div className="report-actions">
                <button type="button" onClick={() => void actOnReport(report, 'reviewed')}>
                  Mark reviewed
                </button>
                <button type="button" onClick={() => void actOnReport(report, 'actioned')}>
                  Actioned
                </button>
                <button type="button" onClick={() => void actOnReport(report, 'dismissed')}>
                  Dismiss
                </button>
              </div>
            </article>
          ))}
          {!reports.length && (
            <div className="panel-empty">
              <HugeiconsIcon icon={Flag02Icon} size={23} />
              <h3>No reports.</h3>
              <p>The moderation queue is clear.</p>
            </div>
          )}
        </div>
      ) : section === 'roadmaps' ? (
        <div className="roadmap-admin-layout">
          <section className="roadmap-quality-list">
            <header>
              <h3>Correction feedback</h3>
              <span>{roadmapFeedback.length}</span>
            </header>
            {roadmapFeedback.map((item) => (
              <article key={item.id}>
                <div>
                  <b>{item.title || item.cache_key.split(':').at(-1)}</b>
                  <span className={`status-pill ${item.quality_status || ''}`}>
                    {item.quality_status || 'private correction'}
                  </span>
                </div>
                <p>{item.feedback}</p>
                <small>
                  {item.username || item.email} · {new Date(item.created_at).toLocaleString()}
                </small>
              </article>
            ))}
            {!roadmapFeedback.length && <p className="admin-empty-copy">No corrections yet.</p>}
          </section>
          <section className="roadmap-quality-list">
            <header>
              <h3>Shared indexes</h3>
              <span>{roadmapEntries.length}</span>
            </header>
            {roadmapEntries.map((item) => (
              <article key={item.cache_key}>
                <div>
                  <b>{item.title}</b>
                  <span className={`status-pill ${item.quality_status}`}>
                    {item.quality_status}
                  </span>
                </div>
                <p>
                  Class {item.grade_level} · {item.curriculum} · {item.exam_track}
                </p>
                <small>
                  {item.reuse_count} reuses · {item.source_model} · updated{' '}
                  {new Date(item.updated_at).toLocaleString()}
                </small>
              </article>
            ))}
            {!roadmapEntries.length && <p className="admin-empty-copy">No cached indexes yet.</p>}
          </section>
        </div>
      ) : (
        <div className="audit-list">
          {audit.map((action) => (
            <article key={action.id}>
              <HugeiconsIcon icon={MoreHorizontalIcon} size={16} />
              <span>
                <b>{action.action.replaceAll('_', ' ')}</b>
                <small>
                  {action.admin_email} · {new Date(action.created_at).toLocaleString()}
                </small>
              </span>
              <code>{action.target_user_id?.slice(0, 8) ?? 'system'}</code>
            </article>
          ))}
          {!audit.length && (
            <div className="panel-empty">
              <HugeiconsIcon icon={ShieldUserIcon} size={23} />
              <h3>No admin actions yet.</h3>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PanelLoading({ label }: { label: string }) {
  return (
    <div className="panel-loading">
      <span />
      <span />
      <span />
      <small>{label}</small>
    </div>
  );
}
